// ═══════════════════════════════════════════════════════════
// Staff Dashboard — request queue overview + action items
// CDS 37 aligned
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IconInbox, IconAlertTriangle, IconClock, IconCircleCheck,
  IconChevronRight, IconRefresh, IconLoader2,
  IconCircleDot, IconPlus, IconArrowRight,
  IconChevronUp, IconChevronDown, IconSelector,
} from '@tabler/icons-react';
import { CLOCK_COLORS, formatDaysRemaining } from '../../../lib/deadlineClock';
import { formatTime } from '../../../lib/format';
import { useRequestStore } from '../../../store/useRequestStore';
import { getRequestersByIds } from '../../../api/requests';
import { listDepartments } from '../../../api/departments';
import { listPendingFeeWaivers } from '../../../api/invoices';
import type { FoiaRequest, Requester } from '../../../api/requests';
import type { PendingFeeWaiver } from '../../../api/invoices';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', acknowledged: 'Acknowledged',
  inProgress: 'In Progress', awaitingRequester: 'Awaiting Requester',
  awaitingPayment: 'Awaiting Payment', pendingReview: 'Pending Review',
  completed: 'Completed', denied: 'Denied', withdrawn: 'Withdrawn', onHold: 'On Hold',
};

const STATUS_COLORS: Record<string, string> = {
  submitted: 'var(--cds-blue-500)', acknowledged: 'var(--cds-green-700)', inProgress: 'var(--color-primary-700)',
  awaitingRequester: 'var(--cds-yellow-500)', awaitingPayment: 'var(--cds-yellow-500)', pendingReview: 'var(--cds-purple-600)',
  completed: '#08A847', denied: 'var(--cds-red-700)', withdrawn: 'var(--color-neutral-500)', onHold: 'var(--color-secondary-700)', draft: 'var(--color-neutral-500)',
};

const CLOSED = new Set(['completed', 'denied', 'withdrawn']);

// ── Stat card ───────────────────────────────────────────
function StatCard({ icon, label, value, accent, href }: {
  icon: React.ReactNode; label: string; value: number; accent: string; href: string;
}) {
  return (
    <Link to={href} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <div style={{
        background: 'white', border: '1px solid var(--color-neutral-200)',
        borderRadius: 4, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12,
        cursor: 'pointer', transition: 'border-color 150ms',
        borderLeft: `3px solid ${accent}`, height: '100%', boxSizing: 'border-box',
      }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = accent)}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-neutral-200)'; e.currentTarget.style.borderLeftColor = accent; }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: 4, background: accent + '14',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent, flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 11, color: 'var(--color-neutral-600)', marginTop: 3, fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</div>
        </div>
      </div>
    </Link>
  );
}

// ── Horizontal bar chart — status breakdown ─────────────
function StatusBars({ requests }: { requests: FoiaRequest[] }) {
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of requests) {
      if (!CLOSED.has(r.status)) c[r.status] = (c[r.status] || 0) + 1;
    }
    return c;
  }, [requests]);

  const items = Object.entries(counts)
    .map(([st, val]) => ({ key: st, label: STATUS_LABELS[st] ?? st, value: val, color: STATUS_COLORS[st] ?? 'var(--color-neutral-500)' }))
    .filter(s => s.value > 0)
    .sort((a, b) => b.value - a.value);

  const max = Math.max(...items.map(i => i.value), 1);
  const total = items.reduce((s, i) => s + i.value, 0);

  if (total === 0) return null;

  return (
    <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)' }}>Open Requests by Status</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{total} open</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(item => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 100, fontSize: 12, fontWeight: 500, color: 'var(--color-neutral-600)', textAlign: 'right', flexShrink: 0 }}>
              {item.label}
            </span>
            <div style={{ flex: 1, height: 24, background: 'var(--color-neutral-100)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
              <div style={{
                height: '100%', borderRadius: 4,
                width: `${(item.value / max) * 100}%`,
                background: item.color, minWidth: 4,
                transition: 'width 300ms ease',
              }} />
            </div>
            <span style={{ width: 32, fontSize: 14, fontWeight: 700, color: item.color, textAlign: 'right', flexShrink: 0 }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Request pipeline — lifecycle stage bars ──────────────
function PipelineBars({ requests }: { requests: FoiaRequest[] }) {
  // Ordered by lifecycle stage, not by count
  const stages = [
    { key: 'submitted', label: 'Submitted', color: 'var(--cds-blue-500)' },
    { key: 'acknowledged', label: 'Acknowledged', color: 'var(--cds-cerulean-500)' },
    { key: 'inProgress', label: 'In Progress', color: 'var(--color-primary-700)' },
    { key: 'awaitingRequester', label: 'Awaiting Requester', color: 'var(--cds-yellow-500)' },
    { key: 'awaitingPayment', label: 'Awaiting Payment', color: 'var(--cds-orange-500)' },
    { key: 'pendingReview', label: 'Pending Review', color: 'var(--cds-purple-600)' },
    { key: 'onHold', label: 'On Hold', color: 'var(--color-secondary-700)' },
    { key: 'completed', label: 'Completed', color: 'var(--cds-green-700)' },
    { key: 'denied', label: 'Denied', color: 'var(--cds-red-700)' },
    { key: 'withdrawn', label: 'Withdrawn', color: 'var(--color-neutral-500)' },
  ];

  const counts: Record<string, number> = {};
  for (const r of requests) counts[r.status] = (counts[r.status] || 0) + 1;

  const items = stages.map(s => ({ ...s, value: counts[s.key] || 0 })).filter(s => s.value > 0);
  const max = Math.max(...items.map(i => i.value), 1);

  if (items.length === 0) return null;

  return (
    <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)' }}>Request Pipeline</span>
        <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{requests.length} total</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(item => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 120, fontSize: 12, fontWeight: 500, color: 'var(--color-neutral-600)', textAlign: 'right', flexShrink: 0 }}>
              {item.label}
            </span>
            <div style={{ flex: 1, height: 24, background: 'var(--color-neutral-100)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 4,
                width: `${(item.value / max) * 100}%`,
                background: item.color, minWidth: 4,
                transition: 'width 300ms ease',
              }} />
            </div>
            <span style={{ width: 32, fontSize: 14, fontWeight: 700, color: item.color, textAlign: 'right', flexShrink: 0 }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Deadline chip ───────────────────────────────────────
function DeadlineChip({ request }: { request: FoiaRequest }) {
  const status = (request.clockStatus ?? 'on-track') as keyof typeof CLOCK_COLORS;
  const colors = CLOCK_COLORS[status] ?? CLOCK_COLORS['on-track'];
  const isClosed = CLOSED.has(request.status);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
      background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`,
      whiteSpace: 'nowrap',
    }}>
      {formatDaysRemaining(request.daysRemaining ?? 0, request.isPaused ?? false, isClosed)}
    </span>
  );
}

// ── Action item card ────────────────────────────────────
function ActionCard({ icon, title, count, accent, items, filterHref }: {
  icon: React.ReactNode; title: string; count: number; accent: string;
  items: Array<{ id: number; number?: string; subject?: string; meta: string }>;
  filterHref: string;
}) {
  const navigate = useNavigate();
  return (
    <div style={{
      background: 'white', border: '1px solid var(--color-neutral-200)',
      borderRadius: 4, overflow: 'hidden', flex: 1, minWidth: 0,
      borderTop: `3px solid ${accent}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: accent }}>{icon}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)' }}>{title}</span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 20, height: 20, padding: '0 4px',
            background: accent, color: 'white', borderRadius: 99, fontSize: 12, fontWeight: 700,
          }}>{count}</span>
        </div>
        <Link to={filterHref} style={{ fontSize: 12, color: accent, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
          View all <IconArrowRight size={12} />
        </Link>
      </div>
      {items.slice(0, 4).map(item => (
        <div key={item.id}
          onClick={() => navigate(`/staff/requests/${item.id}`)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', cursor: 'pointer',
            borderBottom: '1px solid var(--color-neutral-100)',
            transition: 'background 100ms',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-neutral-50)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          {item.number && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: accent, width: 96, flexShrink: 0 }}>
              {item.number}
            </span>
          )}
          <span style={{ fontSize: 14, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-neutral-800)' }}>
            {item.subject ?? `Request #${item.id}`}
          </span>
          <span style={{ fontSize: 12, color: 'var(--color-neutral-500)', flexShrink: 0 }}>{item.meta}</span>
        </div>
      ))}
      {count > 4 && (
        <Link to={filterHref} style={{ display: 'block', padding: '8px 16px', fontSize: 12, color: accent, textDecoration: 'none' }}>
          +{count - 4} more
        </Link>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const requests      = useRequestStore(s => s.requests);
  const loading       = useRequestStore(s => s.loading);
  const error         = useRequestStore(s => s.error);
  const lastFetched   = useRequestStore(s => s.lastFetched);
  const fetchRequests = useRequestStore(s => s.fetchRequests);
  const refresh       = useRequestStore(s => s.refresh);

  const [requesterMap, setRequesterMap] = useState<Map<string, Requester>>(new Map());
  const [deptMap, setDeptMap]           = useState<Record<string, string>>({});
  const [feeWaivers, setFeeWaivers]     = useState<PendingFeeWaiver[]>([]);

  const requestById = new Map(requests.map(r => [r.recordId, r]));

  useEffect(() => {
    fetchRequests({ includeClosd: false });
    listPendingFeeWaivers().then(setFeeWaivers).catch(() => {});
  }, [fetchRequests]);

  const requesterIdKey = requests.map(r => r.requesterId ?? '').join(',');

  useEffect(() => {
    if (!requests.length) return;
    const ids = requests.map(r => r.requesterId).filter((id): id is number => id != null);
    if (ids.length) {
      getRequestersByIds(ids).then(map => {
        const normalized = new Map<string, Requester>();
        for (const [k, v] of map) normalized.set(String(k), v);
        setRequesterMap(normalized);
      });
    }
    listDepartments().then(depts => {
      const map: Record<string, string> = {};
      for (const d of depts) map[String(d.recordId)] = d.name;
      setDeptMap(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requesterIdKey]);

  const newSubmissions = requests.filter(r => r.status === 'submitted');
  const pendingReview  = requests.filter(r => r.status === 'pendingReview');
  const overdue    = requests.filter(r => r.isOverdue && !CLOSED.has(r.status));
  const dueSoon    = requests.filter(r => r.clockStatus === 'due-soon');
  const open       = requests.filter(r => !CLOSED.has(r.status));
  const totalActions = newSubmissions.length + pendingReview.length + feeWaivers.length;

  type SortCol = 'number' | 'subject' | 'requester' | 'department' | 'status' | 'deadline';
  const [sortCol, setSortCol] = useState<SortCol>('deadline');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function handleSort(col: SortCol) {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const sorted = useMemo(() => {
    const arr = [...requests];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortCol) {
        case 'number':    return dir * (a.requestNumber ?? '').localeCompare(b.requestNumber ?? '');
        case 'subject':   return dir * (a.subject ?? '').localeCompare(b.subject ?? '');
        case 'requester': {
          const ra = requesterMap.get(String(a.requesterId))?.displayName ?? '';
          const rb = requesterMap.get(String(b.requesterId))?.displayName ?? '';
          return dir * ra.localeCompare(rb);
        }
        case 'department': {
          const da = a.assignedDeptId ? (deptMap[a.assignedDeptId] ?? '') : '';
          const db = b.assignedDeptId ? (deptMap[b.assignedDeptId] ?? '') : '';
          return dir * da.localeCompare(db);
        }
        case 'status':   return dir * (a.status ?? '').localeCompare(b.status ?? '');
        case 'deadline':
        default: {
          // Urgency: overdue first, then by days remaining
          if (a.isOverdue && !b.isOverdue) return -dir;
          if (!a.isOverdue && b.isOverdue) return dir;
          return dir * ((a.daysRemaining ?? 999) - (b.daysRemaining ?? 999));
        }
      }
    });
    return arr;
  }, [requests, sortCol, sortDir, requesterMap, deptMap]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>Dashboard</h1>
          <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '4px 0 0' }}>
            {loading ? 'Loading…' : error ? `Error: ${error}` : `Refreshed ${formatTime(lastFetched ?? new Date())}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm"
            onClick={() => { refresh(); listPendingFeeWaivers().then(setFeeWaivers).catch(() => {}); }}
            disabled={loading}>
            {loading ? <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <IconRefresh size={14} />} Refresh
          </button>
          <Link to="/staff/requests/new" className="btn btn-primary btn-sm">
            <IconPlus size={14} /> New Request
          </Link>
        </div>
      </div>

      {/* Top section: 2×2 stat cards (left) + New Submissions panel (right) */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'stretch' }}>
        {/* 2×2 stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 10, flexShrink: 0, width: 360 }}>
          <StatCard icon={<IconInbox size={16} />}         label="Open Requests"  value={open.length}    accent="var(--color-primary-700)" href="/staff/requests" />
          <StatCard icon={<IconAlertTriangle size={16} />} label="Overdue"        value={overdue.length} accent="var(--cds-red-700)" href="/staff/requests?filter=overdue" />
          <StatCard icon={<IconClock size={16} />}         label="Due This Week"  value={dueSoon.length} accent="var(--cds-yellow-500)" href="/staff/requests?filter=due-soon" />
          <StatCard icon={<IconCircleCheck size={16} />}   label="Needs Action"   value={totalActions}   accent="var(--cds-purple-600)" href="/staff/requests?filter=needs-action" />
        </div>

        {/* New Submissions panel */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ActionCard
            icon={<IconInbox size={16} />}
            title="New Submissions"
            count={newSubmissions.length}
            accent="var(--cds-yellow-500)"
            filterHref="/staff/requests?filter=submitted"
            items={newSubmissions.map(r => ({ id: r.recordId, number: r.requestNumber, subject: r.subject, meta: formatTime(new Date(r.dateSubmitted)) }))}
          />
        </div>
      </div>

      {/* Request table — takes remaining height, rows scroll internally */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-700)' }}>
            Active Requests
          </span>
          <Link to="/staff/requests" style={{ fontSize: 12, color: 'var(--color-primary-700)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            View all <IconChevronRight size={12} />
          </Link>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              {([
                ['number',     'Request #'],
                ['subject',    'Subject'],
                ['requester',  'Requester'],
                ['department', 'Department'],
                ['status',     'Status'],
                ['deadline',   'Deadline'],
              ] as [SortCol, string][]).map(([col, label]) => (
                <th key={col} style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => handleSort(col)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit',
                      fontFamily: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
                    }}
                  >
                    {label}
                    {sortCol === col
                      ? (sortDir === 'asc' ? <IconChevronUp size={11} /> : <IconChevronDown size={11} />)
                      : <IconSelector size={11} style={{ opacity: 0.35 }} />}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.recordId} onClick={() => navigate(`/staff/requests/${r.recordId}`)}>
                <td style={{ textAlign: 'center' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--color-primary-700)' }}>
                    {r.requestNumber}
                  </span>
                </td>
                <td style={{ maxWidth: 280, textAlign: 'center' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
                    {r.status === 'pendingReview' && <IconCircleDot size={12} style={{ color: 'var(--cds-purple-600)', flexShrink: 0 }} />}
                    {r.status === 'submitted' && <IconCircleDot size={12} style={{ color: 'var(--cds-yellow-500)', flexShrink: 0 }} />}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subject}</span>
                  </div>
                </td>
                <td style={{ color: 'var(--color-neutral-600)', textAlign: 'center' }}>
                  {r.requesterId
                    ? (requesterMap.get(String(r.requesterId))?.displayName ?? '—')
                    : (r.isAnonymous ? 'Anonymous' : '—')}
                </td>
                <td style={{ fontSize: 12, color: 'var(--color-neutral-500)', textAlign: 'center' }}>
                  {r.assignedDeptId ? (deptMap[r.assignedDeptId] ?? 'Unassigned') : 'Unassigned'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                    background: (STATUS_COLORS[r.status] ?? 'var(--color-neutral-500)') + '14',
                    color: STATUS_COLORS[r.status] ?? 'var(--color-neutral-500)',
                  }}>
                    {STATUS_LABELS[r.status] ?? r.status}
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}><DeadlineChip request={r} /></td>
              </tr>
            ))}
            {!loading && requests.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
                  No active requests.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
